import React, { createContext, useContext, useState, useEffect } from 'react';
import { getLedgerPositionStorageKey } from '../containers/Main/Ledgers/LedgerProducts/ledger-products-utils';
import { usePortfolioProfiles } from './usePortfolioProfiles';

export { DEFAULT_FAMILY_MEMBERS } from './usePortfolioProfiles';

const PortfolioContext = createContext();

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

export const createNewFamilyMember = (memberId, displayName) => ({
  id: memberId,
  name: `${displayName}'s Portfolio`,
  displayName: displayName,
  customizable: true,
  icon: 'Person',
  color: getRandomColor(),
  createdAt: new Date().toISOString()
});

const MEMBER_COLORS = ['var(--badge-text)', 'var(--success)', 'var(--warning)', 'var(--accent)', 'var(--badge-text)', 'var(--danger)', 'var(--success)', 'var(--warning)'];

const getRandomColor = () => {
  return MEMBER_COLORS[Math.floor(Math.random() * MEMBER_COLORS.length)];
};

// Remount on account changes so pending requests and cached selections from
// the previous account cannot populate the next account's portfolio picker.
export const PortfolioProvider = ({ children, userId }) => (
  <PortfolioStateProvider key={userId || 'signed-out'} userId={userId}>
    {children}
  </PortfolioStateProvider>
);

const PortfolioStateProvider = ({ children, userId }) => {
  const {
    activePortfolio, familyMembers, switchPortfolio, persistMember, deleteMember,
    portfoliosLoading, portfolioSyncError, refreshPortfolios,
  } = usePortfolioProfiles(userId);
  const [portfolioData, setPortfolioData] = useState({});

  // Load portfolio data and settings from localStorage
  useEffect(() => {
    const loadPortfolioData = () => {
      try {
        // Load existing ledger data and migrate if needed
        const legacyData = localStorage.getItem('ledgerData');
        const portfolioDataRaw = localStorage.getItem('portfolioData');

        if (portfolioDataRaw) {
          const parsedData = JSON.parse(portfolioDataRaw);
          setPortfolioData(parsedData);
        } else if (legacyData) {
          // Migrate legacy data to new structure
          const parsedLegacyData = JSON.parse(JSON.parse(legacyData));
          const migratedData = {
            self: {
              ledgerData: parsedLegacyData || [],
              watchlist: JSON.parse(localStorage.getItem('watchlistData') || '[]'),
              settings: {}
            }
          };
          setPortfolioData(migratedData);
          savePortfolioData(migratedData);
        } else {
          // Initialize with just self portfolio
          const initialData = {
            self: {
              ledgerData: [],
              watchlist: [],
              settings: {}
            }
          };
          setPortfolioData(initialData);
        }
      } catch (error) {
        setPortfolioData({});
      }
    };

    loadPortfolioData();
  }, []);

  const savePortfolioData = (data) => {
    try {
      localStorage.setItem('portfolioData', JSON.stringify(data));
      setPortfolioData(data);
    } catch (error) {
    }
  };

  const updatePortfolioData = (portfolioId, dataType, newData) => {
    setPortfolioData(currentData => {
      const updatedData = {
        ...currentData,
        [portfolioId]: {
          ...currentData[portfolioId],
          [dataType]: newData
        }
      };
      try {
        localStorage.setItem('portfolioData', JSON.stringify(updatedData));
      } catch (error) {
      }
      return updatedData;
    });
  };

  // Called only after the server confirms deletion. Keep unrelated portfolio
  // data while invalidating every cache that could bring deleted trades back.
  const clearJournalData = ({ scope, portfolioId } = {}) => {
    if (scope !== 'all' && scope !== 'portfolio') {
      throw new Error('A journal deletion scope is required.');
    }
    if (scope === 'portfolio' && (
      typeof portfolioId !== 'string' || !portfolioId.trim() || portfolioId === 'family'
    )) {
      throw new Error('Choose an individual portfolio to clear its journal.');
    }

    try {
      localStorage.removeItem('ledgerData');
      let userId = localStorage.getItem('userId');
      try {
        userId = JSON.parse(localStorage.getItem('userSession') || '{}')?.user?.id || userId;
      } catch (error) {
      }

      if (userId) {
        const defaultKey = getLedgerPositionStorageKey(userId);
        const userPrefix = defaultKey.slice(0, defaultKey.lastIndexOf(':') + 1);
        const scopedKeys = new Set([
          getLedgerPositionStorageKey(userId, portfolioId),
          getLedgerPositionStorageKey(userId, 'family'),
          ...(portfolioId === 'self' ? [defaultKey] : []),
        ]);
        const keysToRemove = [];
        for (let index = 0; index < localStorage.length; index += 1) {
          const key = localStorage.key(index);
          if (key && (scope === 'all' ? key.startsWith(userPrefix) : scopedKeys.has(key))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    } catch (error) {
    }

    setPortfolioData(currentData => {
      const updatedData = { ...currentData };
      const portfolioIds = scope === 'all'
        ? Object.keys(currentData)
        : [portfolioId, 'family'];

      portfolioIds.forEach(id => {
        if (currentData[id] || id === portfolioId) {
          updatedData[id] = { ...currentData[id], ledgerData: [] };
        }
      });

      try {
        localStorage.setItem('portfolioData', JSON.stringify(updatedData));
      } catch (error) {
        // If the browser cannot save the cleared data, discard the stale cache.
        try {
          localStorage.removeItem('portfolioData');
        } catch (storageError) {
        }
      }
      return updatedData;
    });
  };

  const getPortfolioData = (portfolioId, dataType) => {
    return portfolioData[portfolioId]?.[dataType] || [];
  };

  const getAllPortfolioData = (dataType) => {
    const allData = [];
    Object.keys(familyMembers).forEach(key => {
      const member = familyMembers[key];
      if (member.id !== 'family') {
        const data = getPortfolioData(member.id, dataType);
        if (data && data.length > 0) {
          allData.push(...data.map(item => ({ ...item, portfolioId: member.id, portfolioName: member.displayName })));
        }
      }
    });
    return allData;
  };

  const updateFamilyMemberLabel = async (memberId, newDisplayName) => {
    const member = familyMembers[memberId];
    if (!member?.customizable) throw new Error('Choose a custom portfolio to rename.');
    return persistMember({
      ...member,
      displayName: newDisplayName.trim(),
      name: `${newDisplayName.trim()}'s Portfolio`,
    });
  };

  const addFamilyMember = async (displayName) => {
    const uniqueId = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const memberId = `member_${uniqueId}`;
    const newMember = createNewFamilyMember(memberId, displayName.trim());
    await persistMember(newMember);
    updatePortfolioData(memberId, 'ledgerData', []);
    return memberId;
  };

  const removeFamilyMember = async (memberId) => {
    if (memberId === 'self' || memberId === 'family') {
      return false; // Cannot remove self or family portfolio
    }

    await deleteMember(memberId);
    setPortfolioData(currentData => {
      const { [memberId]: removedData, ...remainingData } = currentData;
      try {
        localStorage.setItem('portfolioData', JSON.stringify(remainingData));
      } catch (_) { /* Cache is optional. */ }
      return remainingData;
    });

    return true;
  };

  const value = {
    activePortfolio,
    portfolioData,
    switchPortfolio,
    updatePortfolioData,
    clearJournalData,
    getPortfolioData,
    getAllPortfolioData,
    updateFamilyMemberLabel,
    addFamilyMember,
    removeFamilyMember,
    familyMembers,
    portfoliosLoading,
    portfolioSyncError,
    refreshPortfolios,
    isFamily: activePortfolio === 'family'
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};
